import useHttpProvider from "@/common/hooks/use-http";
import { ResponseError } from "@/common/models/error";
import { Country, State } from "@/common/models/places";
import { AuthenticationProvider } from "@/common/providers/authentication";
import * as PlacesServices from "@/common/services/places";
import { notifySuccess } from "@/components/ui/sonner";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import * as ProfileServices from "../services/profiles";

const FormSchema = z.object({
	email: z.string().email("Enter a valid email"),
	firstName: z.string().min(1, "First name is required"),
	lastName: z.string().min(1, "Last name is required"),
	mobileNumber: z.string({
		message: "Phone number is required",
	}),
	countryId: z.string({
		message: "Country is required",
	}),
	stateId: z.string().min(1, "State is required"),
	city: z.string({
		message: "City is required",
	}),
	streetAddress: z.string({
		message: "Street address is required",
	}),
	zipCode: z.string({
		message: "Zip Code is required",
	}),
	image: z.instanceof(File).optional(),
});

function useEditProfile() {
	const user = AuthenticationProvider.user!;
	const { http } = useHttpProvider();

	const form = useForm({
		resolver: zodResolver(FormSchema),
		defaultValues: {},
	});

	const [countries, setCountries] = useState<Country[]>([]);
	const [states, setStates] = useState<State[]>([]);

	const [preview, setPreview] = useState<string | undefined>();

	async function onSubmit(data: z.infer<typeof FormSchema>) {
		try {
			const {
				firstName,
				lastName,
				streetAddress,
				mobileNumber,
				city,
				stateId,
				zipCode,
				image,
			} = data;
			const fullName = `${firstName} ${lastName}`.trim();
			const profilePictureUrl = await ProfileServices.updateProfile(
				http,
				firstName,
				lastName,
				streetAddress,
				mobileNumber,
				city,
				stateId,
				zipCode,
				image
			);

			if (image) {
				AuthenticationProvider.updateInformation(
					profilePictureUrl,
					fullName,
					() => {
						notifySuccess("Profile", "Profile successfully updated!");

						setTimeout(() => {
							window.location.reload();
						}, 500);
					}
				);
			}

			AuthenticationProvider.updateInformation(undefined, fullName, () => {
				notifySuccess("Profile", "Profile successfully updated!");

				setTimeout(() => {
					window.location.reload();
				}, 500);
			});

			notifySuccess("Profile", "Profile successfully updated!");
		} catch (error) {
			console.log((error as ResponseError).detail);
		}
	}

	const handleProfilePictureChange = (
		e: React.ChangeEvent<HTMLInputElement>
	) => {
		const file = (e.target.files ? Array.from(e.target.files) : [])[0];
		form.setValue("image", file);
		setPreview(URL.createObjectURL(file));
	};

	async function getCountries() {
		const countries = await PlacesServices.getCountries(http);
		setCountries(countries);
		return countries;
	}

	async function getStates(countryId: string) {
		const states = await PlacesServices.getStatesByCountryId(
			http,
			parseInt(countryId)
		);
		setStates(states.filter((state) => state.name !== "South Australia"));

		if (!states.some((s) => s.id.toString() === form.getValues("stateId"))) {
			form.setValue("stateId", "");
		}
	}

	async function getProfile() {
		const { profile, address } = await ProfileServices.getProfile(http);

		const countries = await getCountries();
		const countryId = countries.length ? countries[0].id.toString() : "";

		await getStates(countryId);

		setTimeout(() => {
			form.reset({
				countryId,
				stateId: address?.stateId ? address.stateId.toString() : "",
				city: address?.city ?? "",
				email: user!.email,
				firstName: profile?.firstName || "",
				lastName: profile?.lastName || "",
				mobileNumber: profile?.mobileNumber ?? "",
				streetAddress: address?.streetAddress ?? "",
				zipCode: address?.zipCode ?? "",
			});
		}, 100);
	}

	useEffect(() => {
		if (user) {
			getProfile();
		}
	}, [user]);

	return {
		user,

		countries,
		states,

		preview,
		handleProfilePictureChange,

		form,
		onSubmit,
	};
}

export default useEditProfile;
